<template>
    <input type="text"
           placeholder="Введите сумму"
           v-model="displayValue"
           @input="onInput"
           @blur="onBlur"
           :disabled="isDisabled"/>
</template>

<script setup lang="ts">
import {ref, computed, watch, defineProps, defineEmits} from 'vue';

// Определяем интерфейс свойств
interface FormattedNumberInputProps {
    modelValue?: string | number;
    min?: number;
    max?: number;
    isDisabled?: boolean;
}

// Получаем свойства
const props = defineProps<FormattedNumberInputProps>();
const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void;
}>();

// Локальное значение
const localValue = ref<string>(props.modelValue?.toString() || '');

// Следим за изменением modelValue
watch(() => props.modelValue, (newValue) => {
    localValue.value = newValue?.toString() || '';
});

// Функции для форматирования
const formatNumber = (value: string) => {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const unformatNumber = (value: string) => {
    return value.replace(/,/g, '');
};

// Ограничение значений по min/max
const clampValue = (value: number) => {
    if (props.min !== undefined && value <= props.min) return props.min;
    if (props.max !== undefined && value >= props.max) return props.max;
    return value;
};

// Вычисляемое свойство для отображения
const displayValue = computed({
    get() {
        return formatNumber(localValue.value);
    },
    set(newValue) {
        let rawValue = unformatNumber(newValue);
        let numericValue = Number(rawValue);

        if (!isNaN(numericValue)) {
            numericValue = clampValue(numericValue);
            localValue.value = numericValue.toString();
            emit('update:modelValue', numericValue.toString());
        }
    },
});

// Обработчик ввода
const onInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    displayValue.value = target.value;
};

// Обработчик потери фокуса
const onBlur = () => {
    let numericValue = Number(unformatNumber(localValue.value));
    if (!isNaN(numericValue)) {
        numericValue = clampValue(numericValue);
        localValue.value = numericValue.toString();
        emit('update:modelValue', numericValue.toString());
    }
};
</script>
